// ── Speech Engine (Cartesia Sonic-3) ──────────────────────────────────────
// Usa Cartesia para TTS con Web Audio API.
// Fallback a Web Speech API si Cartesia falla.
// Textos largos se dividen automáticamente en fragmentos de ≤180 chars
// para evitar el timeout de Vercel (10s) y palabras cortadas.

let _audioCtx      = null;
let _currentSource = null;
let _cancelled     = false;

export function getSharedAudioCtx() {
  if (!_audioCtx || _audioCtx.state === "closed") {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
}

async function speakCartesia(text, character, onStart, onEnd, witnessName, witnessGender) {
  return new Promise(async resolve => {
    const done = () => { if (onEnd) onEnd(); resolve(); };
    try {
      const res = await fetch("/api/cartesia", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), character, witnessName, witnessGender }),
      });
      if (_cancelled) { done(); return; }
      if (!res.ok) throw new Error(`Cartesia error ${res.status}`);
      const arrayBuffer = await res.arrayBuffer();
      if (_cancelled) { done(); return; }
      const ctx = getSharedAudioCtx();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      if (_cancelled) { done(); return; }
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      _currentSource = source;
      if (onStart) onStart();
      source.onended = () => { _currentSource = null; done(); };
      source.start(0);
    } catch (e) {
      console.warn("Cartesia TTS falló, usando Web Speech:", e.message);
      speakWebSpeech(text, character, onStart, done);
    }
  });
}

function speakWebSpeech(text, character, onStart, onEnd) {
  if (!window.speechSynthesis) { if (onEnd) onEnd(); return; }
  window.speechSynthesis.cancel();
  const sentences = (text.match(/[^.!?]+[.!?]+/g) || [text]).slice(0, 3);
  let idx = 0;
  function speakNext() {
    if (idx >= sentences.length) { if (onEnd) onEnd(); return; }
    const utt = new SpeechSynthesisUtterance(sentences[idx++]);
    utt.lang = "es-EC";
    utt.rate = 1.1;
    if (idx === 1 && onStart) onStart();
    utt.onend = speakNext;
    window.speechSynthesis.speak(utt);
  }
  speakNext();
}

// ── Dividir texto en fragmentos seguros (≤180 chars) ─────────────────────
// 1. Divide por puntuación (.!?)
// 2. Subdivide las largas por comas
// 3. Si sigue larga, corta por palabras
function splitSentences(text) {
  const MAX = 180;

  // Paso 1: dividir por puntuación final
  const bySentence = (text.match(/[^.!?]+[.!?]+/g) || [text])
    .map(s => s.trim()).filter(s => s.length >= 3);

  // Paso 2: subdividir las largas por coma
  const byComma = [];
  for (const sentence of bySentence) {
    if (sentence.length <= MAX) { byComma.push(sentence); continue; }
    const parts = sentence.split(/,\s*/);
    let current = "";
    for (const part of parts) {
      const candidate = current ? current + ", " + part : part;
      if (candidate.length > MAX && current) {
        byComma.push(current.trim());
        current = part;
      } else {
        current = candidate;
      }
    }
    if (current.trim()) byComma.push(current.trim());
  }

  // Paso 3: si aún hay fragmentos largos, cortar por palabras
  const final = [];
  for (const chunk of byComma) {
    if (chunk.length <= MAX) { final.push(chunk); continue; }
    const words = chunk.split(" ");
    let curr = "";
    for (const word of words) {
      const candidate = curr ? curr + " " + word : word;
      if (candidate.length > MAX && curr) {
        final.push(curr.trim());
        curr = word;
      } else {
        curr = candidate;
      }
    }
    if (curr.trim()) final.push(curr.trim());
  }

  return final.filter(s => s.length >= 3);
}

// ─── API PÚBLICA ──────────────────────────────────────────────────────────

export function stopSpeech() {
  _cancelled = true;
  if (_currentSource) {
    try { _currentSource.stop(); } catch (_) {}
    _currentSource = null;
  }
  if (window.speechSynthesis) window.speechSynthesis.cancel();
  setTimeout(() => { _cancelled = false; }, 50);
}

// Textos cortos (≤180 chars): una sola llamada.
// Textos largos: pipeline por fragmentos seguros.
export async function speakText(text, character, onStart, onEnd, witnessName, witnessGender) {
  if (!text?.trim()) { if (onEnd) onEnd(); return; }
  _cancelled = false;
  if (text.trim().length <= 180) {
    await speakCartesia(text, character, onStart, onEnd, witnessName, witnessGender);
    return;
  }
  const sentences = splitSentences(text.trim());
  if (sentences.length <= 1) {
    await speakCartesia(text.trim().slice(0, 180), character, onStart, onEnd, witnessName, witnessGender);
    return;
  }
  await speakPipeline(sentences, character, onStart, onEnd, witnessName, witnessGender);
}

// Pipeline TTS: genera todos los fragmentos en paralelo, reproduce en orden.
export async function speakPipeline(sentences, character, onStart, onEnd, witnessName, witnessGender) {
  if (!sentences?.length) { if (onEnd) onEnd(); return; }
  _cancelled = false;

  const bufferPromises = sentences.map(s =>
    fetch("/api/cartesia", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: s.trim(), character, witnessName, witnessGender }),
    })
    .then(r => r.ok ? r.arrayBuffer() : null)
    .then(ab => ab ? getSharedAudioCtx().decodeAudioData(ab) : null)
    .catch(() => null)
  );

  const playBuffer = (audioBuffer, isFirst, onFirstStart) => new Promise(resolve => {
    const ctx = getSharedAudioCtx();
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    _currentSource = source;
    if (isFirst && onFirstStart) onFirstStart();
    source.onended = () => { _currentSource = null; resolve(); };
    source.start(0);
  });

  let started = false;
  for (let i = 0; i < bufferPromises.length; i++) {
    if (_cancelled) break;
    const audioBuffer = await bufferPromises[i];
    if (!audioBuffer || _cancelled) continue;
    const isFirst = !started;
    if (isFirst) started = true;
    await playBuffer(audioBuffer, isFirst, onStart);
  }
  if (onEnd) onEnd();
}

// Prefetch de un solo fragmento
export async function prefetchTTS(text, character, witnessName, witnessGender) {
  if (!text?.trim()) return null;
  try {
    const res = await fetch("/api/cartesia", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: text.trim(), character, witnessName, witnessGender }),
    });
    if (!res.ok) return null;
    const arrayBuffer = await res.arrayBuffer();
    return await getSharedAudioCtx().decodeAudioData(arrayBuffer);
  } catch { return null; }
}
